import { Button } from '@chakra-ui/button';
import { Card, CardBody, CardHeader } from '@chakra-ui/card';
import { Badge, Box, Heading, HStack, Text, VStack } from '@chakra-ui/layout';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  IconButton,
  Progress,
  Skeleton,
  SkeletonText,
  Switch,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { useData } from '@grcs/context/DataContext';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { usePageTime } from '@grcs/hook/usePageTime';
import { useToast } from '@grcs/hook/useToast';
import {
  ArticleProfile,
  HomeworkProfile,
  HomeworkV2Profile,
  StudentProfile,
} from '@grcs/types/DataTypes';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { parseQuestions } from '@grcs/utils/content/parseQuestions';
import { downloadFile } from '@grcs/utils/storage/databaseStorageLoader';
import { FILE_DB_BUCKET_NAME } from '@grcs/views/const';
import { DoQuestions } from '@grcs/views/shared/components/Homework/DoQuestions';
import { ReadingWorkspace } from './ReadingWorkspace';
import { usePronunciationSelection } from './usePronunciationSelection';
import { Questions } from '@grcs/views/shared/components/Homework/Questions';
import { StudentSelectModalEnhanced as StudentSelectModal } from '@grcs/views/StudentSelectModal.enhanced';
import React, { useEffect, useState } from 'react';
import {
  FiArrowLeft,
  FiBook,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiEyeOff,
  FiVolume2,
} from 'react-icons/fi';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';

export enum MODE {
  PREVIEW = 'preview',
  PUBLISH = 'publish',
  READING = 'reading',
}

interface IHomeworkPreviewProps {
  mode: MODE;
  doQuestion?: boolean;
}

export const HomeworkPreview: React.FC<IHomeworkPreviewProps> = ({ mode }) => {
  const [readingUnlocked, setReadingUnlocked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPinyin, setShowPinyin] = useState<boolean>(false);
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [enableSpeech, setEnableSpeech] = useState<boolean>(false);
  const [content, setContent] = useState<ArticleProfile>();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const {
    selection: speechSelection,
    clearSelection: clearSpeechSelection,
    onDoubleClick: handleSpeechDoubleClick,
  } = usePronunciationSelection(enableSpeech);

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const { supabase } = useData();
  const [{ user_token, full_name, email }] = useLocalStorage('user', {});
  const { year, classId, contentId, homeworkId } = useParams();
  const { successToast, errorToast } = useToast();
  const [homework, setHomework] = useState<HomeworkV2Profile>();
  const [results, setResults] = useState<Map<number, boolean>>(new Map());
  const [isStudentSelectOpen, setIsStudentSelectOpen] = useState(false);
  const [studentList, setStudentList] = useState<StudentProfile[]>([]);
  const [className, setClassName] = useState<string>('');

  const navigate = useNavigate();

  // Selection only reveals the speaker. Start playback from its explicit click
  // so the user controls when audio plays, including on touch devices.
  const pronounceSelection = () => {
    if (!enableSpeech || !speechSelection) return;

    const utterance = new SpeechSynthesisUtterance(speechSelection.text);
    // Prefer an installed Chinese voice; otherwise ask the browser for zh-CN.
    const chineseVoice = window.speechSynthesis
      .getVoices()
      .find((voice) => /^zh(?:-|_)/i.test(voice.lang));

    utterance.lang = chineseVoice?.lang || 'zh-CN';
    if (chineseVoice) utterance.voice = chineseVoice;
    // Replace any previous speech instead of queuing repeated button presses.
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    // Dismiss the assist without changing the browser's native text highlight.
    clearSpeechSelection();
  };

  usePageTime({
    userToken: user_token,
    homeworkToken: homework?.homework_token,
    mode,
  });

  // Clear cache function for debugging
  const clearHomeworkCache = () => {
    const cacheKeys = Object.keys(sessionStorage).filter(
      (key) =>
        key.startsWith('homework_content_') || key.startsWith('homework_'),
    );
    cacheKeys.forEach((key) => sessionStorage.removeItem(key));
    console.log('🧹 Cleared homework cache entries:', cacheKeys.length);
  };

  // Clear cache on component unmount to prevent stale data
  useEffect(() => {
    return () => {
      // Optional: Clear cache when leaving homework
      // clearHomeworkCache();
    };
  }, []);

  // Helper function to check if all questions are answered
  const areAllQuestionsAnswered = (): {
    allAnswered: boolean;
    total: number;
    answeredCount: number;
  } => {
    if (!content?.question)
      return { allAnswered: false, total: 0, answeredCount: 0 };

    const questions = parseQuestions(content.question);
    const totalQuestions = questions.length;
    const answeredQuestions = results.size;

    return {
      allAnswered: answeredQuestions >= totalQuestions,
      total: totalQuestions,
      answeredCount: answeredQuestions,
    };
  };

  useEffect(() => {
    const fetchContent = async () => {
      if (!supabase || !contentId) return;

      // Check cache first with improved error handling
      const cacheKey = `homework_content_${contentId}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);

        if (cached) {
          const cachedData = JSON.parse(cached);
          const isExpired = Date.now() - cachedData.timestamp > 10 * 60 * 1000; // 10 minutes

          if (!isExpired && cachedData.content) {
            console.log('📋 Loading homework content from cache');
            setContent(cachedData.content);
            setProgress(100);
            setLoading(false);
            return;
          } else if (isExpired) {
            // Clean up expired cache
            sessionStorage.removeItem(cacheKey);
          }
        }
      } catch (e) {
        console.warn('Cache error, falling back to fresh fetch:', e);
        // Clean up corrupted cache
        sessionStorage.removeItem(cacheKey);
      }

      try {
        console.log('🚀 Fetching fresh content for ID:', contentId);
        const startTime = Date.now();
        setLoading(true);
        setProgress(25);

        // Direct Supabase query
        const { data: contentData, error: contentError } = await supabase
          .from('library')
          .select('*')
          .eq('id', contentId)
          .single();

        setProgress(75);

        if (contentError) {
          console.error('Content fetch error:', contentError);
          throw new Error('Error fetching homework content');
        }

        if (contentData) {
          setContent(contentData as ArticleProfile);

          // Cache the result with error handling
          try {
            const cacheData = {
              content: contentData,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log('✅ Content loaded and cached successfully');
          } catch (e) {
            console.warn('Failed to cache content data:', e);
            // Continue without caching - not critical
          }
        }

        setProgress(100);
        const endTime = Date.now();
        console.log(`⏱️ Content fetch completed in ${endTime - startTime}ms`);
      } catch (error) {
        console.error('Error fetching homework content data:', error);
        errorToast({
          title: 'Error loading homework',
          description: 'Failed to load homework content. Please try again.',
        });
      } finally {
        setLoading(false);
      }
    };

    if (contentId) {
      fetchContent();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentId, supabase]); // Depend on contentId and supabase

  const handleToggle = () => setShowPinyin(!showPinyin);
  const handleToggleSpeech = () => {
    setEnableSpeech((enabled) => {
      if (enabled) {
        window.speechSynthesis?.cancel();
        clearSpeechSelection();
      }
      return !enabled;
    });
  };
  const handleAnswerToggle = () => {
    if (mode === MODE.PREVIEW) {
      setShowAnswer(!showAnswer);
    }
  };

  const handleDownload = async (filename: string) => {
    downloadFile({
      filename: filename,
      bucket: FILE_DB_BUCKET_NAME,
      supabase: supabase,
    });
  };

  const handlePrePublish = async () => {
    // get all students by class id
    const { data: studentData, error: studentError } = await supabase
      .from('students')
      .select('*')
      .eq('class_token', classId!);

    if (studentError) {
      errorToast({
        title: 'Error getting all students from the class',
        description: studentError.message,
      });
      return;
    }

    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('*')
      .eq('class_token', classId!)
      .single();
    if (classError) {
      errorToast({
        title: 'Error getting class name',
        description: classError.message,
      });
      return;
    }
    setClassName(classData?.class_name || '');
    setStudentList(studentData as StudentProfile[]);
    setIsStudentSelectOpen(true);
  };

  const handlePrePublishReturn = () => {
    setIsStudentSelectOpen(false);
    handleBack();
  };

  const handlePrePublishComplete = async (students_index: Set<number>) => {
    const selectedStudents = studentList.filter((_, index) =>
      students_index.has(index),
    );
    handlePublish(selectedStudents);
  };

  const handlePublish = async (studentData: StudentProfile[]) => {
    try {
      let homeworkData = await getOrCreateHomework();
      let homeworkNeededStudentData = await getHomeworkNeededStudentData(
        homeworkData,
        studentData,
      );

      if (homeworkNeededStudentData.length === 0) {
        successToast({
          title: 'Homework has been published to all students',
          description: 'Homework has been published to all students',
        });
        navigate(`/teacher/${year}/class/${classId}/home`);
        return;
      }

      await insertHomeworkToStudents(homeworkData, homeworkNeededStudentData);
      successToast({
        title: 'Homework published successfully',
        description: 'Homework has been published to all students',
      });
      navigate(`/teacher/${year}/class/${classId}/home`);
      await sendNotificationEmails(homeworkNeededStudentData);
    } catch (error) {
      errorToast({
        title: 'Error publishing homework',
        description: (error as any).message,
      });
    }
  };

  // Create or get homework if it already exists
  const getOrCreateHomework = async () => {
    const { data: existedHomeworkData } = await supabase
      .from(TABLE_NAMES.HOMEWORK)
      .select('homework_token')
      .eq('article_token', content?.id)
      .eq('class_token', classId);

    if (existedHomeworkData && existedHomeworkData.length > 0) {
      return existedHomeworkData[0] as HomeworkProfile;
    } else {
      const { data: newHomeworkData, error: homeworkError } = await supabase
        .from(TABLE_NAMES.HOMEWORK)
        .insert({
          publisher_name: full_name,
          publisher_token: user_token,
          class_token: classId,
          title: content?.title,
          article_token: content?.id,
          grade: content?.grade,
          chapter: content?.chapter,
          created_at: new Date(),
        });
      if (homeworkError) {
        throw new Error(homeworkError.message);
      }
      return newHomeworkData![0] as HomeworkProfile;
    }
  };

  // Get students who have not received the homework
  const getHomeworkNeededStudentData = async (
    homeworkData: HomeworkProfile,
    studentData: StudentProfile[],
  ) => {
    const { data: existedHomeworkStudentData } = await supabase
      .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
      .select('student_token')
      .eq('homework_token', homeworkData.homework_token);

    const existedStudentTokens = new Set(
      existedHomeworkStudentData?.map((data: any) => data.student_token),
    );

    return studentData.filter(
      (student: StudentProfile) =>
        !existedStudentTokens.has(student.student_token),
    );
  };

  // Insert homework to students
  const insertHomeworkToStudents = async (
    homeworkData: HomeworkProfile,
    homeworkNeededStudentData: StudentProfile[],
  ) => {
    const created_at = new Date();
    const bulkUploadHomeWorkStudentData = homeworkNeededStudentData?.map(
      (student: StudentProfile) => ({
        homework_token: homeworkData.homework_token,
        student_token: student.student_token,
        class_token: classId,
        created_at,
        completed: false,
        full_name: student.first_name + ' ' + student.last_name,
        article_token: content?.id,
      }),
    );

    const { error: homeworkStudentError } = await supabase
      .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
      .insert(bulkUploadHomeWorkStudentData);

    if (homeworkStudentError) {
      throw new Error(homeworkStudentError.message);
    }
  };

  // Send notification emails to students
  const sendNotificationEmails = async (
    homeworkNeededStudentData: StudentProfile[],
  ) => {
    const studentEmailsInfo = homeworkNeededStudentData?.map(
      (student: any) => ({
        email_address: {
          address: student.email,
          name: student.first_name + ' ' + student.last_name,
        },
      }),
    );

    const publisherEmailInfo = {
      email_address: {
        address: email,
        name: full_name,
      },
    };

    const articleInfo = {
      title: content?.title,
      grade: content?.grade,
    };

    const { data, error } = await supabase.functions.invoke('send-email', {
      body: { className, studentEmailsInfo, publisherEmailInfo, articleInfo },
    });

    if (error) {
      console.error('Failed to send email notifications:', error);
      // Show warning but don't block the publish flow
      errorToast({
        title: 'Email notification failed',
        description:
          'Homework published successfully, but email notifications could not be sent.',
      });
    } else {
      console.log('Email notifications sent successfully:', data);
    }
  };

  const handleQuestionResult = (questionIndex: number, answer: boolean) => {
    setResults((previous) => new Map(previous).set(questionIndex, answer));
  };

  const handleQuestionRetry = () => {
    setResults(new Map());
  };

  const handlePrint = () => {
    // Save original title
    const originalTitle = document.title;

    // Set page title to article title for print
    document.title = content?.title || 'Homework';

    // Use CSS to hide everything except printable content during print
    const style = document.createElement('style');
    style.id = 'print-only-style';
    style.textContent = `
      @media print {
        body * {
          visibility: hidden;
        }
        #printable-content, #printable-content * {
          visibility: visible;
        }
        #printable-content {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          padding-bottom: 40px;
        }
        #printable-content .do-questions-progress {
          display: none !important;
        }
        .print-footer {
          display: block !important;
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          text-align: center;
          font-size: 10px;
          color: #666;
          padding: 8px 0;
        }
      }
    `;
    document.head.appendChild(style);

    // Trigger browser's native print
    window.print();

    // Restore original title and remove the style after printing
    setTimeout(() => {
      document.title = originalTitle;
      const styleEl = document.getElementById('print-only-style');
      if (styleEl) {
        styleEl.remove();
      }
    }, 1000);
  };

  useEffect(() => {
    const fetchHomework = async () => {
      if (!homeworkId) return;

      // Check cache first with improved error handling
      const cacheKey = `homework_${homeworkId}`;
      try {
        const cached = sessionStorage.getItem(cacheKey);

        if (cached) {
          const cachedData = JSON.parse(cached);
          const isExpired = Date.now() - cachedData.timestamp > 10 * 60 * 1000; // 10 minutes

          if (!isExpired && cachedData.homework) {
            console.log('📋 Loading homework from cache');
            setHomework(cachedData.homework);
            return;
          } else if (isExpired) {
            // Clean up expired cache
            sessionStorage.removeItem(cacheKey);
          }
        }
      } catch (e) {
        console.warn('Homework cache error, falling back to fresh fetch:', e);
        // Clean up corrupted cache
        sessionStorage.removeItem(cacheKey);
      }

      try {
        console.log('🚀 Fetching fresh homework for ID:', homeworkId);
        const { data: homeworkData, error: homeworkError } = await supabase
          .from(TABLE_NAMES.HOMEWORK)
          .select('*')
          .eq('homework_token', homeworkId);

        if (homeworkError) {
          throw new Error('Error fetching homework');
        }

        if (homeworkData && homeworkData.length > 0) {
          const homework = homeworkData[0] as HomeworkV2Profile;
          setHomework(homework);

          // Cache the result with error handling
          try {
            const cacheData = {
              homework,
              timestamp: Date.now(),
            };
            sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
            console.log('✅ Homework loaded and cached successfully');
          } catch (e) {
            console.warn('Failed to cache homework data:', e);
            // Continue without caching - not critical
          }
        }
      } catch (error) {
        console.error('Error fetching homework:', error);
      }
    };

    fetchHomework();
  }, [homeworkId, supabase]);

  // finished the test
  const handlePreFinishClick = () => {
    if (submitted || isSubmitting) return;
    if (mode === MODE.READING && !readingUnlocked) return;
    // Validate that all questions are answered
    if (!content?.question) {
      errorToast({
        title: 'No questions found',
        description: 'Cannot submit homework without questions.',
      });
      return;
    }

    // Parse questions to get total count
    const questions = parseQuestions(content.question);
    const totalQuestions = questions.length;
    const answeredQuestions = results.size;

    if (answeredQuestions < totalQuestions) {
      const unansweredCount = totalQuestions - answeredQuestions;
      errorToast({
        title: 'Incomplete Homework',
        description: `Please answer all questions before submitting. You have ${unansweredCount} unanswered question${
          unansweredCount > 1 ? 's' : ''
        }.`,
      });
      return;
    }

    handleFinishClick();
  };

  const handleFinishClick = async () => {
    setIsSubmitting(true);
    try {
      const questionAttemptedData = Object.fromEntries(results);

      const { error: finishError } = await supabase
        .from(TABLE_NAMES.HOMEWORK_STUDENT_RELATION)
        .update([
          {
            completed: true,
            completed_at: new Date(),
            question_attempted: JSON.stringify(questionAttemptedData),
          },
        ])
        .eq('homework_token', homework?.homework_token)
        .eq('student_token', user_token)
        .eq('class_token', homework?.class_token);

      if (finishError) {
        console.error('❌ Error finishing homework:', finishError);
        errorToast({
          title: 'Error finishing homework',
          description: finishError.message,
        });
        return;
      }

      successToast({
        title: 'Homework finished successfully',
        description: 'Homework has been marked as completed',
      });

      setSubmitted(true);
    } catch (error) {
      errorToast({
        title: 'Error finishing homework',
        description:
          error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (mode === MODE.READING) {
      try {
        localStorage.removeItem(
          `reading-check:${user_token}:${homeworkId || contentId}`,
        );
      } catch {
        /* Storage is optional. */
      }
      setReadingUnlocked(false);
      setResults(new Map());
    }
    navigate(-1);
  };

  // Loading state
  if (loading) {
    return (
      <Box bg={bgColor} minH="100vh" p={6}>
        <VStack spacing={6} align="stretch" maxW="4xl" mx="auto">
          {/* Loading Progress */}
          <Card bg={cardBg} shadow="md">
            <CardBody>
              <VStack spacing={4}>
                <Text fontSize="lg" fontWeight="medium">
                  Loading homework...
                </Text>
                <Progress
                  value={progress}
                  size="lg"
                  colorScheme="blue"
                  w="full"
                />
              </VStack>
            </CardBody>
          </Card>

          {/* Skeleton Content */}
          <Card bg={cardBg} shadow="md">
            <CardHeader>
              <Skeleton height="30px" width="60%" />
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <SkeletonText noOfLines={8} spacing="4" />
                <Skeleton height="200px" />
                <SkeletonText noOfLines={4} spacing="4" />
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    );
  }

  if (!content) {
    return (
      <Box bg={bgColor} minH="100vh" p={6}>
        <VStack spacing={6} align="center" justify="center" minH="50vh">
          <Alert status="error" maxW="md">
            <AlertIcon />
            <AlertDescription>
              Failed to load homework content. Please try again.
            </AlertDescription>
          </Alert>
          <Button leftIcon={<FiArrowLeft />} onClick={handleBack}>
            Go Back
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh">
      {speechSelection && (
        // The hook supplies viewport coordinates for the selected text.
        <Tooltip isOpen>
          <IconButton
            data-speech-button
            minW="44px"
            minH="44px"
            aria-label="朗读"
            icon={<FiVolume2 />}
            colorScheme="blue"
            borderRadius="full"
            boxShadow="lg"
            position="fixed"
            left={`${speechSelection.left}px`}
            top={`${speechSelection.top}px`}
            transform="translateX(-50%)"
            zIndex="popover"
            // Keep button focus from collapsing the native selection before click.
            onPointerDown={(event) => event.preventDefault()}
            onClick={pronounceSelection}
          />
        </Tooltip>
      )}
      {/* Enhanced Header */}
      <Box bg={cardBg} shadow="sm" borderBottom="1px" borderColor={borderColor}>
        <VStack
          spacing={0}
          align="stretch"
          maxW="4xl"
          mx="auto"
          p={{ base: 4, md: 6 }}
        >
          {/* Navigation and Title */}
          <HStack spacing={4} mb={4}>
            <Tooltip label="Go back">
              <IconButton
                aria-label="Go back"
                icon={<FiArrowLeft />}
                variant="ghost"
                onClick={handleBack}
                size="sm"
              />
            </Tooltip>
            <VStack align="start" spacing={1} flex={1}>
              <Heading size="lg" color="gray.800">
                {content?.title || 'Loading...'}
              </Heading>
            </VStack>
          </HStack>

          {/* Controls */}
          <HStack spacing={4} gap={3} justify="space-between" flexWrap="wrap">
            {/* Left: Action buttons */}
            <HStack spacing={3}>
              {content?.file_name && (
                <Tooltip label="Download file">
                  <Button
                    leftIcon={<FiDownload />}
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownload(content?.file_name || '')}
                  >
                    Download
                  </Button>
                </Tooltip>
              )}
              <Tooltip label="Print homework">
                <Button
                  leftIcon={<FiDownload />}
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Tooltip>
            </HStack>

            {/* Right: Toggle switches */}
            <HStack spacing={6}>
              <HStack spacing={2} flexWrap="wrap">
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  拼音
                </Text>
                <Switch
                  size="sm"
                  aria-label="拼音"
                  isChecked={showPinyin}
                  onChange={handleToggle}
                  colorScheme="blue"
                />
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  朗读
                </Text>
                <Switch
                  size="sm"
                  aria-label="朗读"
                  isChecked={enableSpeech}
                  onChange={handleToggleSpeech}
                  colorScheme="green"
                />
                {mode === MODE.PREVIEW && (
                  <>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      答案
                    </Text>
                    <Switch
                      size="sm"
                      aria-label="显示答案"
                      isChecked={showAnswer}
                      onChange={handleAnswerToggle}
                      colorScheme="blue"
                    />
                  </>
                )}
                {showPinyin || showAnswer ? (
                  <FiEye size={16} color="blue" />
                ) : (
                  <FiEyeOff size={16} />
                )}
              </HStack>
            </HStack>
          </HStack>
        </VStack>
      </Box>

      {/* {enableSpeech && (
        <Text fontSize="sm" textAlign="center" px={4} pt={3}>
          电脑：双击汉字；手机或平板：长按汉字。浏览器会自动选中文字，再点击喇叭朗读。
        </Text>
      )} */}
      {/* Main Content */}
      <Box
        maxW="4xl"
        mx="auto"
        px={{ base: 3, md: 6 }}
        pt={{ base: 3, md: 6 }}
        pb={0}
        id="printable-content"
      >
        <ReadingWorkspace
          key={`${user_token}:${homeworkId}:${contentId}:${content.updated_at}`}
          content={content}
          reading={mode === MODE.READING}
          completed={submitted || isSubmitting}
          storageKey={`reading-check:${user_token}:${homeworkId || contentId}`}
          showPinyin={showPinyin}
          onTextDoubleClick={handleSpeechDoubleClick}
          onUnlock={setReadingUnlocked}
          onRestart={handleQuestionRetry}
        >
          {mode !== MODE.PREVIEW ? (
            <Box
              as="fieldset"
              disabled={isSubmitting}
              border={0}
              p={0}
              m={0}
              minW={0}
            >
              <DoQuestions
                submitted={submitted}
                timed={mode == MODE.READING}
                value={
                  showPinyin && content.question_pinyin
                    ? content.question_pinyin
                    : content.question
                }
                onRetry={handleQuestionRetry}
                onResult={handleQuestionResult}
              />
            </Box>
          ) : (
            <Questions
              value={
                showPinyin && content.question_pinyin
                  ? content.question_pinyin
                  : content.question
              }
              showAnswer={showAnswer}
            />
          )}
        </ReadingWorkspace>
      </Box>
      <Box
        maxW="4xl"
        mx="auto"
        px={{ base: 3, md: 6 }}
        pt={2}
        pb={{ base: 3, md: 6 }}
      >
        {/* Action Bar */}
        <Card bg={cardBg} shadow="md">
          <CardBody>
            <HStack justify="space-between" align="center" flexWrap="wrap">
              <Button
                leftIcon={<FiArrowLeft />}
                variant="ghost"
                onClick={handleBack}
              >
                Back
              </Button>

              <HStack spacing={1}>
                {mode === MODE.PUBLISH && (
                  <>
                    <Button
                      colorScheme="blue"
                      size="lg"
                      onClick={handlePrePublish}
                      shadow="md"
                    >
                      Publish Homework
                    </Button>
                    <StudentSelectModal
                      isOpen={isStudentSelectOpen}
                      students={studentList}
                      onClose={handlePrePublishReturn}
                      onComplete={handlePrePublishComplete}
                    />
                  </>
                )}
                {mode === MODE.READING && submitted && (
                  <Button
                    colorScheme="blue"
                    onClick={() => navigate('/student/pending')}
                  >
                    返回作业列表
                  </Button>
                )}
                {mode === MODE.READING && readingUnlocked && !submitted && (
                  <VStack align="end" spacing={1}>
                    {/* <Text fontSize="sm">
                      已回答 {results.size} /{' '}
                      {parseQuestions(content.question).length} 题
                    </Text> */}
                    <Button
                      colorScheme="green"
                      leftIcon={<FiCheckCircle />}
                      onClick={handlePreFinishClick}
                      isLoading={isSubmitting}
                      loadingText="提交中"
                      isDisabled={
                        results.size < parseQuestions(content.question).length
                      }
                    >
                      提交作业
                    </Button>
                  </VStack>
                )}
              </HStack>
            </HStack>
          </CardBody>
        </Card>
      </Box>
    </Box>
  );
};
